import { useEffect, useMemo } from "react";

import {
  getDefaultPortalSimListParams,
  portalSimListInfiniteQueryOptions,
} from "@/hooks/usePortalSimListQuery.ts";
import { useGetSimListInfinite } from "@/services/api.ts";
import { isLockRequested } from "@/utils/deviceUtils.tsx";

/**
 * True when any SIM has `simStatusReason === LOCK_REQUESTED` (renewal failed
 * for lack of a payment method). Used for the cockpit "Add a payment method"
 * todo.
 *
 * Returns `undefined` while the answer isn't known yet (still loading, or
 * more pages remain to check) — callers must treat that as "don't know yet",
 * not as "false", or the todo will flash once a later page reveals a match.
 */
export const useHasLockRequested = (): boolean | undefined => {
  const {
    data: simList,
    isSuccess,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useGetSimListInfinite(getDefaultPortalSimListParams(), {
    query: portalSimListInfiniteQueryOptions,
  });

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return useMemo(() => {
    const sims = simList?.pages?.flatMap((page) => page?.simCards ?? []) ?? [];

    if (sims.some((sim) => isLockRequested(sim))) {
      return true;
    }

    if (!isSuccess || hasNextPage) {
      return undefined;
    }

    return false;
  }, [simList, isSuccess, hasNextPage]);
};
